import React from 'react';
import {
  getPlanStats,
  normalizeCategory,
} from '@features/onboarding/components/shared/form-plan/utils';
import { getPlanLogo } from '@features/onboarding/components/shared/form-plan/planLogo';
import { FormPlanDetails } from '@features/onboarding/components/shared/form-plan/FormPlanDetals';
import type { InsurancePlan } from 'src/services/interfaces/insurance';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@components/ui/accordion';

import { cn } from '@utils/index';

import { categoryIcon } from '../constants/assets';

/**
 * The presentational pieces the W2 selection plan cards share — the B2C
 * `ListPlanCard` (logo / premium column / stats `dl` / "Show Plan Details"
 * accordion) rebuilt as W2-OWNED components carrying the SAME class strings.
 *
 * Doctrine (same as `W2SelectionStepLayout`): pure B2C modules are
 * IMPORTED (`getPlanStats`, `getPlanLogo`, `FormPlanDetails`); the B2C card
 * itself is not mountable here — it is react-hook-form + compare-store coupled
 * and carries a Select radio the W2 waive/who's-covered model replaces — so its
 * visual classes are copied instead.
 *
 * GRACEFUL ABSENCE runs through every piece: with no joined `plan` the logo
 * falls back to the static category icon and the stats/details render nothing,
 * so a card without the join still shows icon + name + price + waive + persons
 * (the pinned `usePlanDetails` contract).
 */

const PLACEHOLDER_LOGO = '/images/placeholder-icn.png';

/**
 * Carrier logo (Equitable / Guardian / …) resolved from the joined plan record,
 * falling back to the static category icon when the join is absent or the plan
 * name matches no carrier — never the bare B2C placeholder, which would read as
 * a broken card.
 */
export const PlanLogo: React.FC<{
  plan?: InsurancePlan;
  category: string;
  className?: string;
}> = ({ plan, category, className }) => {
  const brand = plan ? getPlanLogo(plan) : PLACEHOLDER_LOGO;
  const src = brand === PLACEHOLDER_LOGO ? categoryIcon(category) : brand;
  if (!src) return null;
  return (
    <img
      src={src}
      alt=""
      aria-hidden="true"
      className={cn('xl:size-18 size-16 shrink-0 object-contain', className)}
    />
  );
};

/** The "$8.84 / Monthly premium" column (B2C `ListPlanCard:150-159` classes). */
export const PlanPremium: React.FC<{
  amount: string;
  testId?: string;
  className?: string;
}> = ({ amount, testId, className }) => (
  <div
    className={cn(
      'flex flex-col items-center justify-center gap-1 text-center',
      className
    )}
  >
    <span
      data-testid={testId}
      className="text-[22px] leading-7 font-bold text-black"
    >
      {amount}
    </span>
    <span className="text-[11px] leading-3.5 font-medium tracking-[0.02em] text-profile-text-muted">
      Monthly premium
    </span>
  </div>
);

/**
 * The categories `getPlanStats` has a real per-category path map for. Anything
 * else falls into its else-branch, which returns HARDCODED placeholder figures
 * (Network Type PRO / Deductible $3,500 / …). B2C only ever mounts its cards on
 * the five known category screens, but `PlanScreen` renders a `PlanCard` for
 * ANY non-medical category the backend offers — so a future Telehealth/Life/
 * LegalShield offering would print fabricated deductible and copay numbers in an
 * enrollment flow. Render nothing rather than invent benefits.
 */
const STAT_CATEGORIES = new Set([
  'accident',
  'hospital',
  'criticalillness',
  'dental',
  'vision',
  'health',
]);

/**
 * Per-category attribute column (Maximum Benefit / Major Condition Payout / …)
 * — the reused B2C `getPlanStats` data rendered with the B2C `dl` classes.
 * Renders nothing when the join produced no usable values, or when the plan's
 * category has no real stat map (see `STAT_CATEGORIES`).
 */
export const PlanStats: React.FC<{
  plan?: InsurancePlan;
  className?: string;
}> = ({ plan, className }) => {
  if (!plan || !STAT_CATEGORIES.has(normalizeCategory(plan.category))) {
    return null;
  }

  const stats = getPlanStats(plan).filter((s) => s.value && s.value !== '-');
  if (stats.length === 0) return null;

  return (
    <dl
      className={cn(
        'flex h-fit flex-col text-[11px] tracking-[0.02em] text-profile-text-muted',
        className
      )}
    >
      {stats.map(({ label, value }) => (
        <div
          key={label}
          className="flex items-center justify-between gap-2 text-right"
        >
          <dt className="text-[11px] font-medium text-[#b7b1a5]">{label}</dt>
          <dd className="text-[11px] font-semibold text-black">{value}</dd>
        </div>
      ))}
    </dl>
  );
};

/**
 * "Show / Hide Plan Details" — the B2C accordion trigger over the pure B2C
 * `FormPlanDetails`, which renders `longDescription` + the category field groups
 * (Coverage Details / Service Frequencies …) + the Additional Docs chips. Only
 * mounts when the join produced a plan (graceful absence).
 */
export const PlanDetailsAccordion: React.FC<{
  plan?: InsurancePlan;
  planId: number;
}> = ({ plan, planId }) => {
  const [open, setOpen] = React.useState(false);
  if (!plan) return null;

  return (
    <Accordion
      type="single"
      collapsible
      value={open ? 'details' : ''}
      onValueChange={(value) => setOpen(value === 'details')}
      className="mt-4 text-black"
    >
      <AccordionItem
        value="details"
        id={`w2-plan-details-${planId}`}
        className="flex flex-col border-t border-b-0 border-[#ede6da]"
      >
        <AccordionTrigger
          className="w-full items-center p-0 pt-3 pb-0 text-[13px] font-semibold text-[#3b3a34] hover:no-underline"
          iconProps={{ className: 'size-4 shrink-0 transition-transform' }}
        >
          <span>{open ? 'Hide Plan Details' : 'Show Plan Details'}</span>
        </AccordionTrigger>
        <AccordionContent className="bg-white pt-4 text-black">
          <FormPlanDetails plan={plan} />
        </AccordionContent>
      </AccordionItem>
    </Accordion>
  );
};
